import { useState } from 'react';
import { Paperclip, Plus } from 'lucide-react';
import {
  Button,
  EmptyState,
  ErrorState,
  Skeleton,
  StatusBadge,
  SubBar,
  useUndo,
} from '@/design/components';
import { useMe } from '@/auth/useMe';
import { formatDate } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';
import {
  rangeLabel,
  requestStatusLabel,
  requestStatusTone,
  uploadAttachment,
  useAttachmentUrl,
  useRequestMutations,
  useRequests,
  useRequestTypes,
  type HrRequest,
} from './api';

const FIELD = 'min-h-11 w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink';

/**
 * הבקשות שלי (PRD 5.10.3).
 *
 * מה שלא נמצא כאן בכוונה: ת"ז, פרטי בנק וטופס 101. הם מנוהלים
 * בהנהלת החשבונות ולא נשמרים במערכת (PRD 2.7).
 */
export function RequestsPage() {
  const undo = useUndo();
  const { me } = useMe();
  const requests = useRequests();
  const types = useRequestTypes();
  const { create, cancel } = useRequestMutations();

  const [isAdding, setIsAdding] = useState(false);
  const [typeId, setTypeId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (requests.isPending) return <Skeleton className="m-4 h-40" />;
  if (requests.isError) return <ErrorState onRetry={() => void requests.refetch()} />;

  const mine = (requests.data ?? []).filter((item) => item.employee_id === me?.employeeId);
  const selected = types.data?.find((type) => type.id === typeId);

  const submit = (form: FormData) => {
    if (!me?.employeeId || !typeId) return;
    if (selected?.requires_attachment && !file) {
      setError('הסוג הזה דורש לצרף אישור.');
      return;
    }

    setBusy(true);
    setError(null);

    const upload = file ? uploadAttachment(me.employeeId, file) : Promise.resolve(null);

    upload
      .then((path) =>
        create.mutateAsync({
          employee_id: me.employeeId,
          type_id: typeId,
          from_date: String(form.get('from') ?? '') || null,
          to_date: String(form.get('to') ?? '') || null,
          note: String(form.get('note') ?? '').trim() || null,
          attachment_path: path,
        }),
      )
      .then(() => {
        setIsAdding(false);
        setTypeId('');
        setFile(null);
      })
      .catch((caught) => setError(errorMessage(caught)))
      .finally(() => setBusy(false));
  };

  return (
    <div className="grid content-start gap-4">
      <SubBar title="בקשות" subtitle="חופשה, מחלה וכל בקשה אחרת" />

      <div className="grid gap-4 px-4 pb-8">
        {error && (
          <p role="alert" className="m-0 text-body-sm text-bad">
            {error}
          </p>
        )}

        {!isAdding && (
          <Button variant="primary" onClick={() => setIsAdding(true)}>
            <Plus size={20} strokeWidth={1.75} aria-hidden="true" />
            בקשה חדשה
          </Button>
        )}

        {isAdding && (
          <form
            className="grid gap-3 rounded-md border border-line bg-raised p-3"
            onSubmit={(event) => {
              event.preventDefault();
              submit(new FormData(event.currentTarget));
            }}
          >
            <div className="grid gap-1">
              <label htmlFor="type" className="text-body-sm text-muted">
                סוג הבקשה
              </label>
              <select
                id="type"
                value={typeId}
                onChange={(event) => setTypeId(event.target.value)}
                className={FIELD}
                required
              >
                <option value="">בחירה</option>
                {types.data?.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1">
                <label htmlFor="from" className="text-body-sm text-muted">
                  מתאריך
                </label>
                <input id="from" name="from" type="date" className={FIELD} />
              </div>
              <div className="grid gap-1">
                <label htmlFor="to" className="text-body-sm text-muted">
                  עד תאריך
                </label>
                <input id="to" name="to" type="date" className={FIELD} />
              </div>
            </div>

            <div className="grid gap-1">
              <label htmlFor="note" className="text-body-sm text-muted">
                פרטים
              </label>
              <textarea id="note" name="note" rows={2} className={`${FIELD} py-2`} />
            </div>

            {selected?.requires_attachment && (
              <div className="grid gap-1">
                <label htmlFor="file" className="flex items-center gap-2 text-body-sm text-muted">
                  <Paperclip size={18} strokeWidth={1.75} aria-hidden="true" />
                  צירוף אישור
                </label>
                <input
                  id="file"
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                  className="text-body-sm"
                />
                <p className="m-0 text-label text-muted">
                  האישור נגיש רק לך ולמי שמאשר בקשות.
                </p>
              </div>
            )}

            {selected?.blocks_schedule && (
              <p className="m-0 text-label text-muted">
                אם הבקשה תאושר, הימים יסומנו אוטומטית כאילוץ בסידור.
              </p>
            )}

            <div className="flex flex-wrap gap-2">
              <Button variant="primary" type="submit" disabled={busy}>
                {busy ? 'שולח…' : 'שליחת הבקשה'}
              </Button>
              <Button onClick={() => setIsAdding(false)}>ביטול</Button>
            </div>
          </form>
        )}

        {mine.length === 0 && !isAdding && (
          <EmptyState
            title="אין בקשות"
            description="אפשר לבקש חופשה, לדווח על מחלה או לשלוח בקשה כללית."
          />
        )}

        <ul className="m-0 grid list-none gap-2 p-0">
          {mine.map((request) => (
            <li key={request.id}>
              <RequestCard
                request={request}
                onCancel={
                  request.status === 'pending'
                    ? () => {
                        setError(null);
                        undo.remove({
                          id: request.id,
                          message: 'הבקשה בוטלה',
                          perform: () => cancel.mutateAsync(request.id),
                        });
                      }
                    : undefined
                }
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function RequestCard({
  request,
  showName,
  onCancel,
  children,
}: {
  request: HrRequest;
  showName?: boolean;
  onCancel?: () => void;
  children?: React.ReactNode;
}) {
  const attachment = useAttachmentUrl(request.attachment_path);

  return (
    <div className="grid gap-2 rounded-md border border-line bg-raised p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-title font-medium text-ink">
          {request.hr_request_types?.name}
          {showName && request.employees && (
            <span className="ms-2 text-body-sm font-normal text-muted">
              {request.employees.first_name} {request.employees.last_name}
            </span>
          )}
        </span>
        <StatusBadge tone={requestStatusTone[request.status]}>
          {requestStatusLabel[request.status]}
        </StatusBadge>
      </div>

      <p className="m-0 text-body-sm text-muted">
        {rangeLabel(request.from_date, request.to_date)}
        {request.from_date && ' · '}
        הוגשה {formatDate(request.created_at)}
      </p>

      {request.note && <p className="m-0 text-body">{request.note}</p>}

      {attachment.data && (
        <a
          href={attachment.data}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex w-fit items-center gap-1 text-body-sm text-ink"
        >
          <Paperclip size={16} strokeWidth={1.75} aria-hidden="true" />
          לצפייה באישור
        </a>
      )}

      {onCancel && (
        <div>
          <Button variant="secondary" onClick={onCancel}>
            ביטול הבקשה
          </Button>
        </div>
      )}

      {children}
    </div>
  );
}
